export default function RussellvilleTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Russellville Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Russellville travelers actually move helps us anticipate the passport urgencies that arise here. From ATU study-abroad deadlines to nuclear industry conference schedules, the Arkansas River Valley operates on a travel rhythm shaped by education and energy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">LIT — Clinton National Airport, Little Rock</h3>
                <p className="text-gray-400 text-xs">Little Rock — about 100 miles from Russellville via I-40</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Clinton National Airport in Little Rock is the primary commercial gateway for Russellville travelers. It offers nonstop connections to major hubs including Dallas-Fort Worth, Atlanta, Charlotte, and Houston, with connections to international destinations in Europe, Latin America, and Asia. Nuclear industry professionals, faculty, and ATU researchers heading overseas typically depart from LIT.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> LIT-based connections to Europe and Asia require a valid passport with at least six months of remaining validity. Check your expiration as soon as your conference or program is confirmed — not the week before departure.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Arkansas Tech University Study Abroad</h3>
                <p className="text-gray-400 text-xs">ATU International Education Office, Russellville</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Arkansas Tech University sends students abroad for semester exchanges, summer programs, and faculty-led trips to Europe, Latin America, and Asia. Application deadlines for study-abroad programs often fall in October and February, but many students — especially first-time applicants — do not think about their passport until after acceptance. Standard post office processing in Russellville can take 2–6 months, far exceeding most program orientation timelines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants require an in-person acceptance clerk visit. We guide ATU students through the Pope County acceptance clerk locations, required forms, and timeline — so they do not miss their program orientation.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flashlight-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Arkansas Nuclear One — Industry Travel</h3>
                <p className="text-gray-400 text-xs">Russellville, AR — only nuclear plant in Arkansas</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Arkansas Nuclear One (ANO) employs hundreds of engineers, technicians, and nuclear professionals who travel internationally for IAEA regulatory meetings, international nuclear energy conferences, peer plant inspections in France and Japan, and supplier visits in South Korea and Germany. These assignments often come with 2–4 week lead times — and the nuclear industry does not delay for a missing passport.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> ANO professionals with international assignments should verify passport validity as soon as travel is announced. Many countries require six months of validity beyond your return date — a passport expiring in three months is effectively invalid for most international destinations.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Church Mission &amp; Volunteer Travel</h3>
                <p className="text-gray-400 text-xs">Pope County churches, Russellville mission teams</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Russellville and Pope County have a vibrant church and mission community, with dozens of congregations sending teams annually to Central America, Southeast Asia, and Africa. Mission trip participant lists are typically finalized two to four weeks before departure — leaving minimal time for passport applications. First-time applicants on mission teams are common, and the compressed timeline makes professional expedited service essential.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Mission trip organizers should build passport verification into the team selection process — not the final two weeks. Check all participant passports as soon as the team is confirmed.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-pin-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">100 Miles from Little Rock — We Close the Distance</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Russellville residents are about 95 miles from the Arkansas Passport Center in Hot Springs. Fast Passport Center eliminates that trip entirely — start online, ship overnight, and we handle the submission through our reserved courier access.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}